<template>
  <div
    class="group absolute z-20 -translate-x-1/2 -translate-y-1/2"
    :style="positionStyle"
  >
    <button
      type="button"
      class="flex size-8 touch-none select-none items-center justify-center rounded-full border-2 border-accent bg-bg-elevated/95 font-mono text-xs font-bold text-text-primary shadow-md transition-[border-color,background-color] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent sm:size-9"
      :class="editable ? 'cursor-move hover:bg-bg-surface' : 'cursor-default hover:bg-bg-surface'"
      :data-group-id="group.id"
      :aria-disabled="!editable"
      :aria-label="markerAriaLabel"
      :title="markerTitle"
      @pointerdown="onPointerDown"
      @click="onClick"
      @keydown="onKeydown"
    >
      {{ markerNumber }}
    </button>

    <GvgMapPointPopover
      :label="displayLabel"
      :player-names="playerNames"
      :placement="popoverPlacement"
      :movable="editable"
      :action-label="editable ? messages.tagMarker.movableAction : null"
    />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { defineLocaleCatalog } from '@/shared/i18n'
import { useLocaleMessages } from '@/shared/composables/useI18n'
import type { RaidGroup, RaidMapGroupPosition } from '../../types'
import GvgMapPointPopover from './GvgMapPointPopover.vue'
import { gvgMapEn } from './locales/en.ts'
import { gvgMapRu } from './locales/ru.ts'

export interface MapMarkerNudge {
  readonly xDeltaBasisPoints: number
  readonly yDeltaBasisPoints: number
}

export interface GvgMapGroupMarkerProps {
  readonly group: RaidGroup
  readonly position: RaidMapGroupPosition
  readonly playerNames: ReadonlyArray<string>
  readonly editable: boolean
}

const props = defineProps<GvgMapGroupMarkerProps>()

const emit = defineEmits<{
  (event: 'move-start', pointerEvent: PointerEvent): void
  (event: 'nudge', value: MapMarkerNudge): void
  (event: 'edit'): void
}>()

const messages = useLocaleMessages(defineLocaleCatalog({ en: gvgMapEn, ru: gvgMapRu }))

const positionStyle = computed<Readonly<Record<string, string>>>(() => ({
  left: `${props.position.xBasisPoints / 100}%`,
  top: `${props.position.yBasisPoints / 100}%`,
}))
const popoverPlacement = computed<'above' | 'below'>(() =>
  props.position.yBasisPoints > 6500 ? 'above' : 'below',
)

const markerNumber = computed<string>(() => props.group.stableId.trim() || String(props.group.orderIndex + 1))
const displayLabel = computed<string>(() => {
  const label = props.group.label.trim()
  if (label !== '') return label
  return messages.value.groupMarker.fallbackLabel(markerNumber.value)
})

const markerTitle = computed<string>(() => {
  const roster = props.playerNames.length > 0
    ? props.playerNames.join(', ')
    : messages.value.groupMarker.fallbackTitle
  return `${displayLabel.value}: ${roster}`
})

const markerAriaLabel = computed<string>(() => {
  const action = props.editable ? messages.value.groupMarker.editPrompt : ''
  return `${markerTitle.value}. ${messages.value.groupMarker.positionPercent(Math.round(props.position.xBasisPoints / 100), Math.round(props.position.yBasisPoints / 100))}${action}`
})

function onPointerDown(event: PointerEvent): void {
  if (!props.editable || event.button !== 0) return
  event.preventDefault()
  emit('move-start', event)
}

function onClick(event: MouseEvent): void {
  if (!props.editable || event.detail !== 0) return
  emit('edit')
}

function onKeydown(event: KeyboardEvent): void {
  if (!props.editable) return
  const step = event.shiftKey ? 500 : 100
  if (event.key === 'ArrowLeft') emit('nudge', { xDeltaBasisPoints: -step, yDeltaBasisPoints: 0 })
  else if (event.key === 'ArrowRight') emit('nudge', { xDeltaBasisPoints: step, yDeltaBasisPoints: 0 })
  else if (event.key === 'ArrowUp') emit('nudge', { xDeltaBasisPoints: 0, yDeltaBasisPoints: -step })
  else if (event.key === 'ArrowDown') emit('nudge', { xDeltaBasisPoints: 0, yDeltaBasisPoints: step })
  else return
  event.preventDefault()
}
</script>